// ====================================================================================
// COMPONENT: SurveyDialog (Fix: Safe Icons & White Aesthetic)
// Self-contained modal with all logic and icons.
// ====================================================================================
import { useState } from "react";
import { ClipboardList, Link as LinkIcon, Copy, CheckCircle } from "lucide-react";

const SurveyDialog = ({ isOpen, onClose, surveyData }) => {
  const [copiedIndex, setCopiedIndex] = useState(null);

  if (!isOpen) return null;

  const surveys = [
    { name: 'Candidate Survey', url: surveyData.candidate_survey_url },
    { name: 'Peer Survey', url: surveyData.peer_survey_url },
    { name: 'Public Survey', url: surveyData.public_survey_url },
  ].filter(s => s.url);

  const handleCopy = (url, index) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    }).catch(err => console.error('Failed to copy:', err));
  };

  return (
    // Backdrop
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="absolute inset-0" onClick={onClose} />

      {/* Glass Modal Content */}
      <div className="relative bg-slate-900/90 backdrop-blur-xl border border-slate-700/50 p-8 rounded-xl w-full max-w-2xl shadow-2xl animate-in zoom-in-95 duration-200">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 bg-slate-800/50 rounded-lg border border-slate-700/50">
            <ClipboardList className="text-white" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white leading-none">Survey Links</h2>
            <p className="text-xs text-slate-400 mt-1 font-medium">Distribute these to gather campaign intelligence</p>
          </div>
        </div>

        <div className="space-y-3">
          {surveys.map((survey, index) => (
            <div
              key={index}
              className="bg-slate-950/30 p-1 rounded-lg border border-slate-800/50 flex items-center gap-3 pl-4 pr-1 py-1 group hover:border-slate-600 transition-colors"
            >
              {/* Icon & Label */}
              <div className="flex items-center gap-3 min-w-[140px]">
                <LinkIcon size={14} className="text-slate-500 group-hover:text-white transition-colors" />
                <span className="text-xs font-bold text-slate-300 group-hover:text-white transition-colors uppercase tracking-wide">{survey.name}</span>
              </div>

              {/* URL Display (Monochrome) */}
              <div className="flex-grow font-mono text-[10px] text-slate-400 group-hover:text-slate-200 truncate opacity-80 transition-colors">
                {survey.url}
              </div>

              {/* Action Button (White/Monochrome) */}
              <button
                onClick={() => handleCopy(survey.url, index)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-md transition-all duration-300 shadow-sm border ${
                  copiedIndex === index
                    ? 'bg-white text-slate-900 border-white'
                    : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white hover:bg-slate-700 hover:border-slate-500'
                }`}
              >
                {copiedIndex === index ? (
                  <> <CheckCircle size={12} /> Copied </>
                ) : (
                  <> <Copy size={12} /> Copy </>
                )}
              </button>
            </div>
          ))}
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-white transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveyDialog;
